import { useCallback, useEffect, useMemo, useState } from 'react'
import type { RiskReductionMeasure } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { RiskReductionMeasureFormData } from './RiskReductionMeasureForm.schema'
import { toRiskReductionMeasureFormData } from './riskReductionMeasuresUtils'

type EditRiskReductionMeasureDescriptionDialogProps = {
  isOpen: boolean
  onClose: () => void
  riskReductionMeasure: RiskReductionMeasure
  onSave: (data: RiskReductionMeasureFormData) => void
  isSaving: boolean
}

export function EditRiskReductionMeasureDescriptionDialog({
  isOpen,
  onClose,
  riskReductionMeasure,
  onSave,
  isSaving,
}: EditRiskReductionMeasureDescriptionDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [description, setDescription] = useState(
    riskReductionMeasure.description ?? ''
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setDescription(riskReductionMeasure.description ?? '')
  }, [isOpen, riskReductionMeasure.description])

  const canSubmit = useMemo(
    () => description.trim().length > 0,
    [description]
  )

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleSubmit = () => {
    if (!canSubmit || isSaving) {
      return
    }

    onSave({
      ...toRiskReductionMeasureFormData(riskReductionMeasure),
      description: description.trim(),
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-lg' showCloseButton={!isSaving}>
        <DialogHeader>
          <DialogTitle>
            {t(
              'riskReductionMeasuresLibraryPage.rrmDetail.editDescriptionDialog.title'
            )}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-2'>
          <Label htmlFor='rrm-edit-description'>
            {t('riskReductionMeasuresLibraryPage.form.description')}{' '}
            <span className='text-destructive'>*</span>
          </Label>
          <Textarea
            id='rrm-edit-description'
            value={description}
            onChange={(event) => {
              setDescription(event.target.value)
            }}
            placeholder={t(
              'riskReductionMeasuresLibraryPage.form.descriptionPlaceholder'
            )}
            rows={5}
          />
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isSaving}>
            {t('riskReductionMeasuresLibraryPage.formDialog.updateButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
